import { FeedbackResolutionStatus, FeedbackVoteFilter, IFeedbackResolveRequest } from './models'

/* --- Geri bildirim sonuçlandırma kuralları (Faz-37 §2, ADR-022) — saf yardımcılar --- */

export const POPUP_TITLE_MAX_LENGTH = 150
export const POPUP_HTML_MAX_LENGTH = 4000
export const INTERNAL_NOTE_MAX_LENGTH = 1000

/** Geri Bildirimler sekmesi oy filtresi; `down` varsayılandır. */
export const DEFAULT_FEEDBACK_VOTE: FeedbackVoteFilter = 'down'

export const FEEDBACK_VOTE_OPTIONS: { value: FeedbackVoteFilter; label: string }[] = [
  { value: 'down', label: '👎 Olumsuz' },
  { value: 'up', label: '👍 Olumlu' },
  { value: 'all', label: 'Tümü' },
]

export const RESOLUTION_STATUS_OPTIONS: { value: FeedbackResolutionStatus; label: string }[] = [
  { value: 'improved', label: 'Geliştirildi' },
  { value: 'closed', label: 'Kapatıldı' },
]

/** Bilinmeyen kod SESSİZCE DÜŞÜRÜLMEZ: ham kod gösterilir. */
export const resolutionStatusLabel = (status: string | null | undefined): string =>
  RESOLUTION_STATUS_OPTIONS.find(option => option.value === status)?.label ?? status ?? '—'

/** Tamamlananlar duyuru filtresi: boş = hepsi (parametre gönderilmez). */
export type AnnouncedFilter = '' | 'yes' | 'no'

export const announcedFilterToQuery = (filter: AnnouncedFilter): boolean | undefined => {
  if (filter === 'yes') return true
  if (filter === 'no') return false
  return undefined
}

export const UNANNOUNCED_CONFIRM_TEXT =
  'Bu geri bildirim bayiye duyuru yapılmadan kapatılacak. Duyurusuz kapatmak istediğinize emin misiniz?'

export const NO_ANNOUNCE_PERMISSION_TEXT = 'Bayiye duyuru yetkiniz yok'

export const ALREADY_RESOLVED_TEXT = 'Bu geri bildirim zaten sonuçlandırılmış'

/** Yeniden açma onayı; duyuru gittiyse geri çekilmediği açıkça yazılır. */
export const reopenConfirmText = (announced: boolean): string =>
  announced
    ? 'Geri bildirim yeniden açılacak ve Geri Bildirimler listesine dönecek. Bayiye giden duyuru geri çekilmez; henüz görmemiş kullanıcılara artık gösterilmez.'
    : 'Geri bildirim yeniden açılacak ve Geri Bildirimler listesine dönecek.'

/**
 * HTML'in görünür metni (doğrulama için): etiketler ve `&nbsp;` atılır. Sunucu da temizleme sonrası
 * görünür metni boş içeriği reddeder; bu yalnız erken uyarıdır.
 */
export const htmlVisibleText = (html: string): string =>
  (html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export interface ResolveFormState {
  /** Seçilmeden kaydedilemez; varsayılan statü YOKTUR. */
  status: FeedbackResolutionStatus | null
  announce: boolean
  popupTitle: string
  popupHtml: string
  internalNote: string
}

/** Duyuru kutusu varsayılan işaretli; yetkisi olmayan admin için işaretsiz başlar. */
export const initialResolveForm = (canAnnounce: boolean): ResolveFormState => ({
  status: null,
  announce: canAnnounce,
  popupTitle: '',
  popupHtml: '',
  internalNote: '',
})

/** Kaydetmeden önceki doğrulama; ilk hata mesajı döner, geçerliyse `null`. */
export const validateResolveForm = (form: ResolveFormState, canAnnounce: boolean): string | null => {
  if (!form.status) return 'Statü seçin: Geliştirildi veya Kapatıldı.'
  if (form.internalNote.length > INTERNAL_NOTE_MAX_LENGTH)
    return `İç not en fazla ${INTERNAL_NOTE_MAX_LENGTH} karakter olabilir.`
  if (!form.announce) return null
  if (!canAnnounce) return NO_ANNOUNCE_PERMISSION_TEXT
  if (!form.popupTitle.trim()) return 'Popup başlığı zorunludur.'
  if (form.popupTitle.trim().length > POPUP_TITLE_MAX_LENGTH)
    return `Popup başlığı en fazla ${POPUP_TITLE_MAX_LENGTH} karakter olabilir.`
  if (!htmlVisibleText(form.popupHtml)) return 'Popup içeriği zorunludur.'
  if (form.popupHtml.length > POPUP_HTML_MAX_LENGTH)
    return `Popup içeriği en fazla ${POPUP_HTML_MAX_LENGTH} karakter olabilir (HTML dahil).`
  return null
}

/** Duyuru yoksa PopupTitle/PopupHtml gövdeye HİÇ girmez; boş iç not `null` gider. */
export const buildResolveRequest = (form: ResolveFormState): IFeedbackResolveRequest => {
  const note = form.internalNote.trim()
  const request: IFeedbackResolveRequest = {
    Status: form.status as FeedbackResolutionStatus,
    InternalNote: note ? note : null,
    Announce: form.announce,
  }
  if (form.announce) {
    request.PopupTitle = form.popupTitle.trim()
    request.PopupHtml = form.popupHtml
  }
  return request
}

const readNumericCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const source = error as Record<string, unknown>
  for (const key of ['Code', 'code', 'httpStatusCode']) {
    const value = Number(source[key])
    if (Number.isFinite(value) && value >= 1000) return value
  }
  return undefined
}

const readDescription = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const source = error as Record<string, unknown>
  for (const key of ['description', 'Message', 'message']) {
    const value = source[key]
    if (typeof value === 'string' && value.trim() && value !== 'OK') return value
  }
  return undefined
}

/**
 * Sonuçlandırma / yeniden açma hatası → ekrandaki metin. ClientProxy `Success=false` yanıtında sayısal
 * `Code`'u taşımaz (yalnız `Message`); kod varsa 1403/1409 sabit metne çevrilir, yoksa sunucu mesajı
 * (1400 doğrulama, 1404 bulunamadı) gösterilir.
 */
export const resolveErrorMessage = (error: unknown): string => {
  const code = readNumericCode(error)
  if (code === 1403) return NO_ANNOUNCE_PERMISSION_TEXT
  if (code === 1409) return ALREADY_RESOLVED_TEXT
  return readDescription(error) ?? 'İşlem tamamlanamadı. Lütfen tekrar deneyin.'
}

/**
 * ClientProxy POST'ta `Data = null` + `Message = "OK"` başarılı yanıtı da hata olarak fırlatır;
 * gövdesiz başarılı uçlarda (yeniden açma, görüldü) bu durum başarı sayılır.
 */
export const isOkPseudoError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const source = error as Record<string, unknown>
  return source.description === 'OK' && source.success !== false
}
